import { useState } from "react";
import { Plus, Pencil, Trash2, Ticket, CircleDollarSign } from "lucide-react";
import { useEntryTypes, EntryMode } from "@/hooks/useEntryTypes";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";

export function EntryTypesCard() {
  const { entryTypes, isLoading, createEntryType, updateEntryType, deleteEntryType } = useEntryTypes();
  
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedEntryType, setSelectedEntryType] = useState<typeof entryTypes[0] | null>(null);
  
  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  const [entryMode, setEntryMode] = useState<EntryMode>("fixed");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const resetForm = () => {
    setName("");
    setAmount("");
    setEntryMode("fixed");
  };

  const handleAdd = async () => {
    if (!name.trim() || !amount) return;
    
    setIsSubmitting(true);
    await createEntryType({
      name: name.trim(),
      amount: parseFloat(amount),
      entry_mode: entryMode,
    });
    setIsSubmitting(false);
    setIsAddDialogOpen(false);
    resetForm();
  };

  const handleEdit = async () => {
    if (!selectedEntryType || !name.trim() || !amount) return;
    
    setIsSubmitting(true);
    await updateEntryType(selectedEntryType.id, {
      name: name.trim(),
      amount: parseFloat(amount),
      entry_mode: entryMode,
    });
    setIsSubmitting(false);
    setIsEditDialogOpen(false);
    resetForm();
  };

  const handleDelete = async () => {
    if (!selectedEntryType) return;
    
    await deleteEntryType(selectedEntryType.id);
    setIsDeleteDialogOpen(false);
  };

  const openEditDialog = (entryType: typeof entryTypes[0]) => {
    setSelectedEntryType(entryType);
    setName(entryType.name);
    setAmount(String(entryType.amount));
    setEntryMode(entryType.entry_mode);
    setIsEditDialogOpen(true);
  };

  const openDeleteDialog = (entryType: typeof entryTypes[0]) => {
    setSelectedEntryType(entryType);
    setIsDeleteDialogOpen(true);
  };

  const handleToggleActive = async (entryType: typeof entryTypes[0]) => {
    await updateEntryType(entryType.id, { is_active: !entryType.is_active });
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-32 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Ticket className="h-5 w-5" />
                Tipos de Entrada
              </CardTitle>
              <CardDescription>
                Cadastre os tipos de entrada que serão usados na abertura de comandas
              </CardDescription>
            </div>
            <Button onClick={() => setIsAddDialogOpen(true)} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Nova Entrada
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {entryTypes.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Ticket className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Nenhum tipo de entrada cadastrado</p>
              <p className="text-sm">Clique em "Nova Entrada" para começar</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Valor</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Ativo</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entryTypes.map((entryType) => (
                  <TableRow key={entryType.id}>
                    <TableCell className="font-medium">{entryType.name}</TableCell>
                    <TableCell>{formatCurrency(entryType.amount)}</TableCell>
                    <TableCell>
                      <Badge variant={entryType.entry_mode === "fixed" ? "default" : "secondary"}>
                        {entryType.entry_mode === "fixed" ? "Entrada Fixa" : "Consumação Mínima"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={entryType.is_active}
                        onCheckedChange={() => handleToggleActive(entryType)}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditDialog(entryType)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openDeleteDialog(entryType)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Novo Tipo de Entrada</DialogTitle>
            <DialogDescription>
              Cadastre um novo tipo de entrada para abertura de comandas
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nome</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Masculino, Feminino, VIP"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="amount">Valor (R$)</Label>
              <Input
                id="amount"
                type="number"
                min="0"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0,00"
              />
            </div>
            <div className="space-y-2">
              <Label>Tipo de Cobrança</Label>
              <Select value={entryMode} onValueChange={(v) => setEntryMode(v as EntryMode)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fixed">
                    <div className="flex items-center gap-2">
                      <CircleDollarSign className="h-4 w-4" />
                      Entrada Fixa
                    </div>
                  </SelectItem>
                  <SelectItem value="minimum_consumption">
                    <div className="flex items-center gap-2">
                      <Ticket className="h-4 w-4" />
                      Consumação Mínima
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {entryMode === "fixed"
                  ? "Valor cobrado na entrada, independente do consumo"
                  : "Se o cliente consumir menos, paga a diferença no fechamento"}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleAdd} disabled={isSubmitting || !name.trim() || !amount}>
              {isSubmitting ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Tipo de Entrada</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Nome</Label>
              <Input
                id="edit-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-amount">Valor (R$)</Label>
              <Input
                id="edit-amount"
                type="number"
                min="0"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Tipo de Cobrança</Label>
              <Select value={entryMode} onValueChange={(v) => setEntryMode(v as EntryMode)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fixed">Entrada Fixa</SelectItem>
                  <SelectItem value="minimum_consumption">Consumação Mínima</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleEdit} disabled={isSubmitting}>
              {isSubmitting ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover Tipo de Entrada</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja remover "{selectedEntryType?.name}"? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
